import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  PromotionSetting, 
  PromotionSettingCreateDto, 
  PromotionSettingUpdateDto,
  PromotionSettingResponse,
  PromotionSettingLatestResponse,
  PromotionFormSetting,
  PromotionFormSettingCreateDto,
  PromotionFormSettingUpdateDto,
  PromotionFormSettingResponse,
  PromotionFormSettingLatestResponse,
  PromotionFormSettingBulkCreateRequest,
  PromotionFormSettingBulkCreateResponse,
  PromotionFormSettingBulkUpdateRequest,
  PromotionFormSettingBulkUpdateResponse
} from "../../modules/shared/model/promotion-settings.interfaces";
import {
  getPromotionSettings,
  getLatestPromotionSetting,
  getPromotionSettingById,
  createPromotionSetting,
  updatePromotionSetting,
  deletePromotionSetting,
  getPromotionFormSettings,
  getLatestPromotionFormSetting,
  getPromotionFormSettingById,
  createPromotionFormSetting,
  updatePromotionFormSetting,
  deletePromotionFormSetting,
  bulkCreatePromotionFormSettings,
  bulkUpdatePromotionFormSettings
} from "../Actions/PromotionSettingActions";

// ===== PROMOTION SETTINGS ASYNC THUNKS =====

export const fetchPromotionSettings = createAsyncThunk("promotionSettings/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getPromotionSettings(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch promotion settings"); 
  }
});

export const fetchLatestPromotionSetting = createAsyncThunk("promotionSettings/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestPromotionSetting(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest promotion setting"); 
  }
});

export const fetchPromotionSettingById = createAsyncThunk("promotionSettings/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getPromotionSettingById(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch promotion setting"); 
  }
});

export const createPromotionSettingAction = createAsyncThunk("promotionSettings/create", async (data: PromotionSettingCreateDto, { rejectWithValue }) => {
  try { 
    return await createPromotionSetting(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create promotion setting"); 
  }
});

export const updatePromotionSettingAction = createAsyncThunk("promotionSettings/update", async ({ id, data }: { id: string; data: PromotionSettingUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updatePromotionSetting(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update promotion setting"); 
  }
});

export const deletePromotionSettingAction = createAsyncThunk("promotionSettings/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deletePromotionSetting(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete promotion setting"); 
  }
});

// ===== PROMOTION FORM SETTINGS ASYNC THUNKS =====

export const fetchPromotionFormSettings = createAsyncThunk("promotionFormSettings/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getPromotionFormSettings(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch promotion form settings"); 
  }
});

export const fetchLatestPromotionFormSetting = createAsyncThunk("promotionFormSettings/fetchLatest", async (_, { rejectWithValue }) => {
  try { 
    return await getLatestPromotionFormSetting(); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch latest promotion form setting"); 
  }
});

export const fetchPromotionFormSettingById = createAsyncThunk("promotionFormSettings/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getPromotionFormSettingById(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch promotion form setting"); 
  }
});

export const createPromotionFormSettingAction = createAsyncThunk("promotionFormSettings/create", async (data: PromotionFormSettingCreateDto, { rejectWithValue }) => {
  try { 
    return await createPromotionFormSetting(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create promotion form setting"); 
  }
});

export const updatePromotionFormSettingAction = createAsyncThunk("promotionFormSettings/update", async ({ id, data }: { id: string; data: PromotionFormSettingUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updatePromotionFormSetting(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update promotion form setting"); 
  }
});

export const deletePromotionFormSettingAction = createAsyncThunk("promotionFormSettings/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deletePromotionFormSetting(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete promotion form setting"); 
  }
});

export const bulkCreatePromotionFormSettingsAction = createAsyncThunk("promotionFormSettings/bulkCreate", async (data: PromotionFormSettingBulkCreateRequest, { rejectWithValue }) => {
  try { 
    return await bulkCreatePromotionFormSettings(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create promotion form settings"); 
  }
});

export const bulkUpdatePromotionFormSettingsAction = createAsyncThunk("promotionFormSettings/bulkUpdate", async (data: PromotionFormSettingBulkUpdateRequest, { rejectWithValue }) => {
  try { 
    return await bulkUpdatePromotionFormSettings(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update promotion form settings"); 
  }
});

// State interface
interface PromotionSettingState {
  // Promotion Settings
  promotionSettings: PromotionSetting[];
  latestPromotionSetting: PromotionSetting | null;
  currentPromotionSetting: PromotionSetting | null;
  promotionSettingsLoading: boolean;
  promotionSettingsError: string | null;
  promotionSettingsTotalCount?: number;
  promotionSettingsCurrentPage: number;
  promotionSettingsHasNextPage: boolean;
  promotionSettingsHasPreviousPage: boolean;
  promotionSettingsSearchQuery: string;
  
  // Promotion Form Settings
  promotionFormSettings: PromotionFormSetting[];
  latestPromotionFormSetting: PromotionFormSetting | null;
  currentPromotionFormSetting: PromotionFormSetting | null;
  promotionFormSettingsLoading: boolean;
  promotionFormSettingsError: string | null;
  promotionFormSettingsTotalCount?: number;
  promotionFormSettingsCurrentPage: number;
  promotionFormSettingsHasNextPage: boolean;
  promotionFormSettingsHasPreviousPage: boolean;
  promotionFormSettingsSearchQuery: string;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: PromotionSettingState = {
  // Promotion Settings
  promotionSettings: [],
  latestPromotionSetting: null,
  currentPromotionSetting: null,
  promotionSettingsLoading: false,
  promotionSettingsError: null,
  promotionSettingsTotalCount: 0,
  promotionSettingsCurrentPage: 1,
  promotionSettingsHasNextPage: false,
  promotionSettingsHasPreviousPage: false,
  promotionSettingsSearchQuery: "",
  
  // Promotion Form Settings
  promotionFormSettings: [],
  latestPromotionFormSetting: null,
  currentPromotionFormSetting: null,
  promotionFormSettingsLoading: false,
  promotionFormSettingsError: null,
  promotionFormSettingsTotalCount: 0,
  promotionFormSettingsCurrentPage: 1,
  promotionFormSettingsHasNextPage: false,
  promotionFormSettingsHasPreviousPage: false,
  promotionFormSettingsSearchQuery: "",
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const promotionSettingSlice = createSlice({
  name: "promotionSettings",
  initialState,
  reducers: {
    // Promotion Settings Reducers
    clearPromotionSettingsError(state) { 
      state.promotionSettingsError = null; 
    },
    clearCurrentPromotionSetting(state) { 
      state.currentPromotionSetting = null; 
    },
    setPromotionSettingsSearchQuery(state, action: PayloadAction<string>) { 
      state.promotionSettingsSearchQuery = action.payload; 
    },
    clearAllPromotionSettings(state) {
      state.promotionSettings = [];
      state.currentPromotionSetting = null;
      state.latestPromotionSetting = null;
      state.promotionSettingsTotalCount = 0;
    },
    updatePromotionSettingFromForm(state, action: PayloadAction<Partial<PromotionSetting>>) {
      if (state.currentPromotionSetting) {
        state.currentPromotionSetting = { ...state.currentPromotionSetting, ...action.payload };
      }
    },
    updateLatestPromotionSettingFromForm(state, action: PayloadAction<Partial<PromotionSetting>>) {
      if (state.latestPromotionSetting) {
        state.latestPromotionSetting = { ...state.latestPromotionSetting, ...action.payload };
      }
    },
    
    // Promotion Form Settings Reducers
    clearPromotionFormSettingsError(state) { 
      state.promotionFormSettingsError = null; 
    },
    clearCurrentPromotionFormSetting(state) { 
      state.currentPromotionFormSetting = null; 
    },
    setPromotionFormSettingsSearchQuery(state, action: PayloadAction<string>) { 
      state.promotionFormSettingsSearchQuery = action.payload; 
    },
    clearAllPromotionFormSettings(state) {
      state.promotionFormSettings = [];
      state.currentPromotionFormSetting = null;
      state.latestPromotionFormSetting = null;
      state.promotionFormSettingsTotalCount = 0;
    },
    updatePromotionFormSettingFromForm(state, action: PayloadAction<Partial<PromotionFormSetting>>) {
      if (state.currentPromotionFormSetting) {
        state.currentPromotionFormSetting = { ...state.currentPromotionFormSetting, ...action.payload };
      }
    },
    updateLatestPromotionFormSettingFromForm(state, action: PayloadAction<Partial<PromotionFormSetting>>) {
      if (state.latestPromotionFormSetting) {
        state.latestPromotionFormSetting = { ...state.latestPromotionFormSetting, ...action.payload };
      }
    },
    
    // Common Form Reducers
    setPromotionFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
  },
  extraReducers: (builder) => {
    // ===== PROMOTION SETTINGS EXTRA REDUCERS =====

    // Fetch All Promotion Settings
    builder
      .addCase(fetchPromotionSettings.pending, (state) => { 
        state.promotionSettingsLoading = true; 
        state.promotionSettingsError = null; 
      })
      .addCase(fetchPromotionSettings.fulfilled, (state, action: PayloadAction<PromotionSettingResponse>) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettings = action.payload.data || []; 
        state.promotionSettingsTotalCount = action.payload.totalCount || 0; 
        state.promotionSettingsCurrentPage = action.payload.page || 1;
        state.promotionSettingsHasNextPage = action.payload.hasNextPage || false;
        state.promotionSettingsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchPromotionSettings.rejected, (state, action: any) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettingsError = action.payload as string; 
      });

    // Fetch Latest Promotion Setting
    builder
      .addCase(fetchLatestPromotionSetting.pending, (state) => { 
        state.promotionSettingsLoading = true; 
        state.promotionSettingsError = null; 
      })
      .addCase(fetchLatestPromotionSetting.fulfilled, (state, action: PayloadAction<PromotionSettingLatestResponse>) => { 
        state.promotionSettingsLoading = false; 
        state.latestPromotionSetting = action.payload; 
        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(fetchLatestPromotionSetting.rejected, (state, action: any) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettingsError = action.payload as string; 
      });

    // Fetch Promotion Setting By ID
    builder
      .addCase(fetchPromotionSettingById.pending, (state) => { 
        state.promotionSettingsLoading = true; 
        state.promotionSettingsError = null; 
      })
      .addCase(fetchPromotionSettingById.fulfilled, (state, action: PayloadAction<PromotionSetting>) => { 
        state.promotionSettingsLoading = false; 
        state.currentPromotionSetting = action.payload; 
      })
      .addCase(fetchPromotionSettingById.rejected, (state, action: any) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettingsError = action.payload as string; 
      });

    // Create Promotion Setting
    builder
      .addCase(createPromotionSettingAction.pending, (state) => { 
        state.promotionSettingsLoading = true; 
        state.promotionSettingsError = null; 
      })
      .addCase(createPromotionSettingAction.fulfilled, (state, action: PayloadAction<PromotionSetting>) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettings.unshift(action.payload); 
        state.promotionSettingsTotalCount = (state.promotionSettingsTotalCount || 0) + 1; 
        state.latestPromotionSetting = action.payload; // Update latest setting
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createPromotionSettingAction.rejected, (state, action: any) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettingsError = action.payload as string; 
      });

    // Update Promotion Setting
    builder
      .addCase(updatePromotionSettingAction.pending, (state) => { 
        state.promotionSettingsLoading = true; 
        state.promotionSettingsError = null; 
      })
      .addCase(updatePromotionSettingAction.fulfilled, (state, action: PayloadAction<PromotionSetting>) => {
        state.promotionSettingsLoading = false;
        const index = state.promotionSettings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.promotionSettings[index] = action.payload;
        if (state.currentPromotionSetting?.id === action.payload.id) state.currentPromotionSetting = action.payload;
        state.latestPromotionSetting = action.payload; // Update latest setting
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updatePromotionSettingAction.rejected, (state, action: any) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettingsError = action.payload as string; 
      });

    // Delete Promotion Setting
    builder
      .addCase(deletePromotionSettingAction.pending, (state) => { 
        state.promotionSettingsLoading = true; 
        state.promotionSettingsError = null; 
      })
      .addCase(deletePromotionSettingAction.fulfilled, (state, action: PayloadAction<string>) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettings = state.promotionSettings.filter(x => x.id !== action.payload); 
        if (state.currentPromotionSetting?.id === action.payload) state.currentPromotionSetting = null; 
        if (state.latestPromotionSetting?.id === action.payload) state.latestPromotionSetting = null;
        state.promotionSettingsTotalCount = Math.max(0, (state.promotionSettingsTotalCount || 0) - 1); 
      })
      .addCase(deletePromotionSettingAction.rejected, (state, action: any) => { 
        state.promotionSettingsLoading = false; 
        state.promotionSettingsError = action.payload as string; 
      });

    // ===== PROMOTION FORM SETTINGS EXTRA REDUCERS =====

    // Fetch All Promotion Form Settings
    builder
      .addCase(fetchPromotionFormSettings.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(fetchPromotionFormSettings.fulfilled, (state, action: PayloadAction<PromotionFormSettingResponse>) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettings = action.payload.data || []; 
        state.promotionFormSettingsTotalCount = action.payload.totalCount || 0; 
        state.promotionFormSettingsCurrentPage = action.payload.page || 1;
        state.promotionFormSettingsHasNextPage = action.payload.hasNextPage || false;
        state.promotionFormSettingsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchPromotionFormSettings.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Fetch Latest Promotion Form Setting
    builder
      .addCase(fetchLatestPromotionFormSetting.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(fetchLatestPromotionFormSetting.fulfilled, (state, action: PayloadAction<PromotionFormSettingLatestResponse>) => { 
        state.promotionFormSettingsLoading = false; 
        state.latestPromotionFormSetting = action.payload; 
        state.lastSavedAt = new Date().toISOString();
      })
      .addCase(fetchLatestPromotionFormSetting.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Fetch Promotion Form Setting By ID
    builder
      .addCase(fetchPromotionFormSettingById.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(fetchPromotionFormSettingById.fulfilled, (state, action: PayloadAction<PromotionFormSetting>) => { 
        state.promotionFormSettingsLoading = false; 
        state.currentPromotionFormSetting = action.payload; 
      })
      .addCase(fetchPromotionFormSettingById.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Create Promotion Form Setting
    builder
      .addCase(createPromotionFormSettingAction.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(createPromotionFormSettingAction.fulfilled, (state, action: PayloadAction<PromotionFormSetting>) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettings.unshift(action.payload); 
        state.promotionFormSettingsTotalCount = (state.promotionFormSettingsTotalCount || 0) + 1; 
        state.latestPromotionFormSetting = action.payload; // Update latest setting
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createPromotionFormSettingAction.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Update Promotion Form Setting
    builder
      .addCase(updatePromotionFormSettingAction.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(updatePromotionFormSettingAction.fulfilled, (state, action: PayloadAction<PromotionFormSetting>) => {
        state.promotionFormSettingsLoading = false;
        const index = state.promotionFormSettings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.promotionFormSettings[index] = action.payload;
        if (state.currentPromotionFormSetting?.id === action.payload.id) state.currentPromotionFormSetting = action.payload;
        state.latestPromotionFormSetting = action.payload; // Update latest setting
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updatePromotionFormSettingAction.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Delete Promotion Form Setting
    builder
      .addCase(deletePromotionFormSettingAction.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(deletePromotionFormSettingAction.fulfilled, (state, action: PayloadAction<string>) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettings = state.promotionFormSettings.filter(x => x.id !== action.payload); 
        if (state.currentPromotionFormSetting?.id === action.payload) state.currentPromotionFormSetting = null; 
        if (state.latestPromotionFormSetting?.id === action.payload) state.latestPromotionFormSetting = null;
        state.promotionFormSettingsTotalCount = Math.max(0, (state.promotionFormSettingsTotalCount || 0) - 1); 
      })
      .addCase(deletePromotionFormSettingAction.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Bulk Create Promotion Form Settings
    builder
      .addCase(bulkCreatePromotionFormSettingsAction.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(bulkCreatePromotionFormSettingsAction.fulfilled, (state, action: PayloadAction<PromotionFormSettingBulkCreateResponse>) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettings.unshift(...action.payload.success); 
        state.promotionFormSettingsTotalCount = (state.promotionFormSettingsTotalCount || 0) + action.payload.success.length; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkCreatePromotionFormSettingsAction.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });

    // Bulk Update Promotion Form Settings
    builder
      .addCase(bulkUpdatePromotionFormSettingsAction.pending, (state) => { 
        state.promotionFormSettingsLoading = true; 
        state.promotionFormSettingsError = null; 
      })
      .addCase(bulkUpdatePromotionFormSettingsAction.fulfilled, (state, action: PayloadAction<PromotionFormSettingBulkUpdateResponse>) => {
        state.promotionFormSettingsLoading = false;
        action.payload.success.forEach(updatedFormSetting => {
          const index = state.promotionFormSettings.findIndex(x => x.id === updatedFormSetting.id);
          if (index !== -1) state.promotionFormSettings[index] = updatedFormSetting;
          if (state.currentPromotionFormSetting?.id === updatedFormSetting.id) state.currentPromotionFormSetting = updatedFormSetting;
        });
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkUpdatePromotionFormSettingsAction.rejected, (state, action: any) => { 
        state.promotionFormSettingsLoading = false; 
        state.promotionFormSettingsError = action.payload as string; 
      });
  }
});

export const { 
  // Promotion Settings Actions
  clearPromotionSettingsError,
  clearCurrentPromotionSetting,
  setPromotionSettingsSearchQuery,
  clearAllPromotionSettings,
  updatePromotionSettingFromForm,
  updateLatestPromotionSettingFromForm,
  
  // Promotion Form Settings Actions
  clearPromotionFormSettingsError,
  clearCurrentPromotionFormSetting,
  setPromotionFormSettingsSearchQuery,
  clearAllPromotionFormSettings,
  updatePromotionFormSettingFromForm,
  updateLatestPromotionFormSettingFromForm,
  
  // Common Form Actions
  setPromotionFormDirty
} = promotionSettingSlice.actions;

export default promotionSettingSlice.reducer;
